/**
 * The EW job page's Tickets and Change orders tabs (EW-5).
 *
 *   • Tickets — this job's field tickets, and "Create ticket": the API builds
 *     its lines from the job's daily logs and rate card. A per_day job tickets
 *     one work day at a time (the day picker); a per_job job has one live
 *     ticket. A live ticket that already exists (409 ticket_exists) is opened
 *     rather than reported as a failure.
 *   • Change orders — this job's change orders, and "New change order" (reason,
 *     optional description); lines are added on the change order's own page.
 *
 * Both read through the office routes, so they need a rate-reading role; a
 * field hand reaches their tickets from the job's phone screen instead.
 */
import { useState } from 'react';
import { FilePlus2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useCreateEwChangeOrder, useCreateEwTicket, useEwChangeOrders, useEwTickets } from '../../hooks/useEwTickets';
import { ewApiError, ewDay, ewRefusal } from '../../lib/earthworks';
import { ewCoSkin, ewTicketSkin } from '../../lib/ew-tickets';
import type { EwChangeOrder, EwTicket } from '../../types/earthworks';
import { ewCoMoney } from './EwChangeOrdersScreen';
import { ewTicketMoney } from './EwTicketsScreen';
import {
  EW_DATE_INPUT,
  EW_INPUT,
  EW_PRIMARY,
  EW_SECONDARY,
  EwBadge,
  EwDialog,
  EwEmpty,
  EwField,
  EwFormError,
  EwPanel,
  EwRegister,
  type EwColumn,
} from './EwBits';

interface BillingJob {
  id: string;
  status: string;
  ticketMode: string | null;
}

export function JobTicketsSection({
  job,
  today,
  canWrite,
  onOpen,
}: {
  job: BillingJob;
  today: string | null;
  canWrite: boolean;
  onOpen: (path: string) => void;
}) {
  const q = useEwTickets({ job_id: job.id });
  const create = useCreateEwTicket();
  const rows = q.data?.data ?? [];
  const perDay = job.ticketMode === 'per_day';
  const [day, setDay] = useState('');
  const [error, setError] = useState<string | null>(null);
  const chosen = day || today || '';
  const mayCreate = canWrite && job.status !== 'cancelled';

  const doCreate = () => {
    setError(null);
    create.mutate({ jobId: job.id, workDate: perDay ? chosen || undefined : undefined }, {
      onSuccess: (r) => {
        const b = r.built;
        toast.success(b ? `Ticket drafted · ${b.drafted} line${b.drafted === 1 ? '' : 's'}${b.unpriced ? `, ${b.unpriced} unpriced` : ''}` : 'Ticket drafted');
        if (b?.flags?.length) toast.warning(b.flags.join(' '), { duration: 15000 });
        if (r.data?.id) onOpen(`/earthworks/tickets/${r.data.id}`);
      },
      onError: (err) => {
        const e = ewApiError(err);
        const existing = typeof e.extra.existingId === 'string' ? e.extra.existingId : null;
        if (e.code === 'ticket_exists' && existing) {
          toast.message(e.message ?? 'This job already has a live ticket. Opening it.');
          onOpen(`/earthworks/tickets/${existing}`);
          return;
        }
        setError(ewRefusal(err, 'The ticket could not be created.'));
      },
    });
  };

  const columns: EwColumn<EwTicket>[] = [
    { key: 'number', header: 'Ticket', render: (t) => <span className="font-mono font-bold text-white">{t.ticketNumber}</span> },
    { key: 'day', header: 'Work day', render: (t) => (t.mode === 'per_day' ? ewDay(t.workDate) : t.modeLabel || 'Whole job') },
    { key: 'status', header: 'Status', render: (t) => <EwBadge skin={ewTicketSkin(t.status)} value={t.status} /> },
    { key: 'lines', header: 'Lines', align: 'right', render: (t) => t.lineCount },
    { key: 'subtotal', header: 'Subtotal', align: 'right', render: (t) => <span className="font-mono">{ewTicketMoney(t).value}</span> },
  ];

  return (
    <EwPanel
      eyebrow="Billing"
      title="Field tickets"
      testId="ew-job-tickets"
      actions={
        mayCreate ? (
          <div className="flex items-center gap-2">
            {perDay && (
              <input type="date" className={EW_DATE_INPUT} aria-label="Ticket day" max={today ?? undefined} value={chosen} onChange={(e) => setDay(e.target.value)} />
            )}
            <button type="button" className={EW_PRIMARY} onClick={doCreate} disabled={create.isPending || (perDay && !chosen)} data-testid="ew-job-create-ticket">
              <FilePlus2 className="size-4" aria-hidden="true" />
              {create.isPending ? 'Building…' : 'Create ticket'}
            </button>
          </div>
        ) : undefined
      }
    >
      {error && <div className="px-4 pt-3"><EwFormError message={error} /></div>}
      <EwRegister
        query={q}
        rows={rows}
        columns={columns}
        rail={(t) => ewTicketSkin(t.status).rail}
        onOpen={(t) => onOpen(`/earthworks/tickets/${t.id}`)}
        subject="field tickets"
        testId="ew-job-ticket-row"
        minWidth="min-w-[640px]"
        empty={<EwEmpty>No tickets on this job yet. {perDay ? 'Each work day is ticketed from its daily log.' : 'The ticket is built from the job\'s daily logs.'}</EwEmpty>}
        card={(t) => (
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-mono text-sm font-bold text-white">{t.ticketNumber}</p>
              <p className="text-xs text-fieldiq-muted">{t.mode === 'per_day' ? ewDay(t.workDate) : 'Whole job'} · {ewTicketMoney(t).value}</p>
            </div>
            <EwBadge skin={ewTicketSkin(t.status)} value={t.status} />
          </div>
        )}
      />
    </EwPanel>
  );
}

export function JobChangeOrdersSection({ job, canWrite, onOpen }: { job: BillingJob; canWrite: boolean; onOpen: (path: string) => void }) {
  const q = useEwChangeOrders({ job_id: job.id });
  const create = useCreateEwChangeOrder();
  const rows = q.data ?? [];
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mayCreate = canWrite && job.status !== 'cancelled' && job.status !== 'completed';

  const doCreate = () => {
    setError(null);
    if (!reason.trim()) return setError('Say what the change is for.');
    create.mutate({ jobId: job.id, body: { reason: reason.trim(), description: description.trim() || null } }, {
      onSuccess: (co) => {
        toast.success(co?.coNumber ? `${co.coNumber} drafted` : 'Change order drafted');
        setOpen(false);
        setReason('');
        setDescription('');
        if (co?.id) onOpen(`/earthworks/change-orders/${co.id}`);
      },
      onError: (err) => setError(ewRefusal(err, 'The change order could not be created.')),
    });
  };

  const columns: EwColumn<EwChangeOrder>[] = [
    { key: 'number', header: 'Change order', render: (c) => <span className="font-mono font-bold text-white">{c.coNumber}</span> },
    { key: 'reason', header: 'Reason', render: (c) => <span className="line-clamp-2">{c.reason || '—'}</span> },
    { key: 'status', header: 'Status', render: (c) => <EwBadge skin={ewCoSkin(c.status)} value={c.status} /> },
    { key: 'total', header: 'Amount', align: 'right', render: (c) => <span className="font-mono">{ewCoMoney(c)}</span> },
  ];

  return (
    <EwPanel
      eyebrow="Billing"
      title="Change orders"
      testId="ew-job-cos"
      actions={
        mayCreate ? (
          <button type="button" className={EW_PRIMARY} onClick={() => { setError(null); setOpen(true); }} data-testid="ew-job-new-co">
            <Plus className="size-4" aria-hidden="true" />
            New change order
          </button>
        ) : undefined
      }
    >
      <EwRegister
        query={q}
        rows={rows}
        columns={columns}
        rail={(c) => ewCoSkin(c.status).rail}
        onOpen={(c) => onOpen(`/earthworks/change-orders/${c.id}`)}
        subject="change orders"
        testId="ew-job-co-row"
        minWidth="min-w-[640px]"
        empty={<EwEmpty>No change orders on this job.</EwEmpty>}
        card={(c) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-mono text-sm font-bold text-white">{c.coNumber}</p>
              <p className="truncate text-xs text-fieldiq-muted">{c.reason} · {ewCoMoney(c)}</p>
            </div>
            <EwBadge skin={ewCoSkin(c.status)} value={c.status} />
          </div>
        )}
      />
      <EwDialog
        open={open}
        title="New change order"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className={EW_SECONDARY} onClick={() => setOpen(false)} disabled={create.isPending}>Cancel</button>
            <button type="button" className={EW_PRIMARY} onClick={doCreate} disabled={create.isPending}>{create.isPending ? 'Creating…' : 'Create draft'}</button>
          </>
        }
      >
        <EwFormError message={error} />
        <div className="space-y-3">
          <EwField label="Reason">
            <input className={EW_INPUT} aria-label="Change order reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </EwField>
          <EwField label="Description (optional)">
            <textarea className={`${EW_INPUT} h-20 py-2`} aria-label="Change order description" value={description} onChange={(e) => setDescription(e.target.value)} />
          </EwField>
          <p className="text-xs text-fieldiq-muted">Lines are added on the change order once it is drafted.</p>
        </div>
      </EwDialog>
    </EwPanel>
  );
}
